/**
 * ⚠️⚠️ EL CÓDIGO DEL FALLO ES EL ÚNICO DATO QUE NOMBRA LA CAUSA SIN ADIVINAR.
 *
 * `format-sync-error.ts` reconoce la causa por la PROSA almacenada, y eso basta
 * para `data_sources.last_error`, que no tiene columna de código. Pero el aviso
 * de importación fallida que lee el cliente no sale de ahí: sale del evento SSE
 * `sync_failed`, cuyo origen es `platform_jobs.error_data` — donde SÍ hay
 * `code`, y donde el `message` puede ser inglés crudo del `createApiError`,
 * jerga de operación, un centinela sin contenido del saneador, o nada.
 *
 * MEDIDO POR LA UI (#2215): el desenlace real de una importación de HyperLiquid
 * fue `STUCK_JOB_REAPED` y el cliente leyó «La importación falló.» sobre una
 * cabecera que decía «Fuente desconocida». El código existía en la base de datos
 * desde el primer segundo.
 *
 * 🔑 QUÉ CATÁLOGO SE USA PARA CADA CAUSA. La pantalla de super-admin ya traduce
 * estos mismos códigos (`platform/sync/sync-failure-reason.ts`),
 * pero su redacción nombra el endpoint RPC y la pantalla donde corregirlo: es
 * para el OPERADOR. Aquí se apunta a la frase que el cliente puede accionar, que
 * en la mitad de los casos es la MISMA clave (`common:monitoring.failureReasons.*`
 * ya es prosa de cliente) y en las dos de cadena es la del propio importador.
 *
 * ⚠️⚠️ EL CÓDIGO NO ES UN CATÁLOGO GENERAL, Y ESO NO SE NEGOCIA. Un fallo con
 * código puede traer un mensaje MÁS informativo que su traducción —el de
 * cabeceras compara las dos listas de columnas, y `formatSyncErrorForDetail`
 * existe precisamente para no cortarlas—, así que sustituir todo mensaje que
 * traiga código cambiaría un defecto por otro. La regla es:
 *
 *   - unos pocos códigos ganan SIEMPRE, porque su mensaje almacenado nunca dice
 *     nada que la traducción no diga (`FAILURE_CODES_THAT_OUTRANK_THE_MESSAGE`);
 *   - el resto sólo se consulta cuando el mensaje almacenado está VACÍO o es uno
 *     de los centinelas sin contenido del saneador del backend.
 */

/**
 * Código de `platform_jobs.error_data.code` → clave traducida que el CLIENTE
 * puede leer. Las claves van cualificadas por espacio de nombres para que
 * resuelvan sea cual sea el `t` del llamante.
 */
const CUSTOMER_FACING_KEY_BY_FAILURE_CODE: Readonly<Record<string, string>> = {
  // El segador mató un trabajo que llevaba demasiado tiempo sin avanzar. Para el
  // operador es «el worker se colgó»; para el cliente, que la importación se
  // detuvo y el sistema la canceló — y que puede volver a lanzarla.
  STUCK_JOB_REAPED: 'common:syncWait.stalledAndCancelled',

  // Dos códigos, una causa: `FILE_NOT_FOUND` es el del `createApiError` del
  // procesador e `IMPORT_FILE_UNAVAILABLE` el del catálogo seguro del backend.
  FILE_NOT_FOUND: 'common:monitoring.failureReasons.fileUnavailable',
  IMPORT_FILE_UNAVAILABLE: 'common:monitoring.failureReasons.fileUnavailable',

  IMPORT_NUMERIC_OUT_OF_RANGE: 'common:monitoring.failureReasons.numericOutOfRange',
  IMPORT_FILE_PROCESSING: 'common:monitoring.failureReasons.fileProcessing',
  IMPORT_NO_CONFIGURATION_MATCHED: 'common:monitoring.failureReasons.noConfigurationMatched',
  IMPORT_MAPPINGS_REQUIRED: 'common:monitoring.failureReasons.mappingsRequired',
  // El superadmin deshabilitó la plantilla del exchange (2026-09-26): el
  // remedio del cliente es soporte, no «elegir el formato» — ya no lo ve.
  IMPORT_TEMPLATE_DISABLED: 'common:monitoring.failureReasons.templateDisabled',

  // Igual que arriba: `UNSUPPORTED_IMPORT_METHOD` viaja en `error_data.code` y
  // `IMPORT_METHOD_NOT_SUPPORTED` es el del catálogo seguro.
  UNSUPPORTED_IMPORT_METHOD: 'common:monitoring.failureReasons.methodNotSupported',
  IMPORT_METHOD_NOT_SUPPORTED: 'common:monitoring.failureReasons.methodNotSupported',

  // ⚠️ Las dos primeras de cadena usan la redacción DEL IMPORTADOR, no la de
  // admin: la de admin dice «revisa las credenciales del endpoint RPC de esa
  // cadena en Redes Blockchain», que es un remedio que el cliente no tiene.
  IMPORT_BLOCKCHAIN_UNAVAILABLE: 'imports:sources.errorModal.blockchainSyncFailed',
  IMPORT_BLOCKCHAIN_ACCESS_DENIED: 'imports:sources.errorModal.blockchainAccessDenied',
  // La del exceso de ritmo ya está escrita para el cliente y se reutiliza tal
  // cual — el mismo criterio que aplica `format-sync-error.ts`.
  IMPORT_BLOCKCHAIN_RATE_LIMITED: 'common:monitoring.failureReasons.blockchainRateLimited',
  // BUG-123: ningún nodo activo puede leer la cartera entera; el barrido se
  // negó a leer a medias. Remedio del cliente: soporte (no nombra el endpoint).
  IMPORT_BLOCKCHAIN_NODE_INCAPABLE: 'common:monitoring.failureReasons.blockchainNodeIncapable',

  IMPORT_GENERIC_FAILURE: 'common:monitoring.failureReasons.generic',
}

/**
 * Los códigos cuyo mensaje almacenado NUNCA añade nada, así que el código manda
 * aunque venga texto.
 *
 *  - `STUCK_JOB_REAPED`: su mensaje es literalmente la bitácora del segador
 *    («no progress update for over 10 minutes — worker likely crashed or hung»),
 *    en inglés y en jerga de operación.
 *  - `FILE_NOT_FOUND` / `IMPORT_FILE_UNAVAILABLE`: lo decidió el #1724 y aquí
 *    sólo se conserva — su mensaje es la ruta del servidor, ya recortada por el
 *    saneador, y la traducción dice lo mismo y además qué hacer.
 */
const FAILURE_CODES_THAT_OUTRANK_THE_MESSAGE: readonly string[] = [
  'STUCK_JOB_REAPED',
  'FILE_NOT_FOUND',
  'IMPORT_FILE_UNAVAILABLE',
]

/**
 * Textos que el backend escribe cuando NO tiene nada seguro que contar:
 * `GENERIC_JOB_ERROR_MESSAGE` y `GENERIC_DB_ERROR_MESSAGE` de
 * `job-error-sanitizer.ts`. Comparación EXACTA sobre el texto recortado, igual
 * que `LOCALIZED_KEY_BY_STORED_MESSAGE`: un mensaje que sólo los CONTENGA lleva
 * más información y se respeta.
 */
const CONTENT_FREE_STORED_MESSAGES: readonly string[] = [
  'Job failed',
  'A database error occurred while processing the job',
]

/**
 * ⚠️⚠️ UN CÓDIGO, DOS NOTICIAS: EL MOTIVO DE LA SIEGA (#2205 dentro de #2215).
 *
 * `STUCK_JOB_REAPED` es un solo código, pero desde #2205 la segadora mata por
 * DOS preguntas distintas y estampa cuál falló en `errorData.reapedState`:
 *
 *   `no-liveness-update`  → el trabajo dejó de escribir: el proceso se detuvo.
 *   `no-measured-advance` → siguió escribiendo, y pasó una hora sin que nada
 *                           medible avanzara.
 *
 * 🔑 POR QUÉ SE DISTINGUEN, y no es cosmética. Primero, porque la frase única
 * —«la importación dejó de avanzar»— es EXACTA para el segundo motivo y falsa en
 * su mecanismo para el primero: ahí la importación no dejó de avanzar, la mataron
 * mientras avanzaba. Decirle a alguien que su trabajo se paró cuando lo que pasó
 * es que el proceso murió es la misma clase de defecto que cierra el #2215: un
 * mensaje que describe una causa mientras ocurrió otra.
 *
 * Y segundo, porque prescriben MOVIMIENTOS distintos, que es la prueba que este
 * repositorio ya aplica para separar `blockchainUnavailable` de
 * `blockchainAccessDenied`: tras una caída del proceso, relanzar ES el remedio;
 * tras una hora sin avanzar, relanzar a ciegas repetirá el mismo muro, y lo
 * honesto es decir que si se repite hay que escribir a Soporte.
 *
 * Una siega anterior a #2205 no trae motivo y cae a la frase genérica, que sigue
 * siendo cierta para las dos.
 */
const KEY_BY_STUCK_JOB_REAP_REASON: Readonly<Record<string, string>> = {
  'no-liveness-update': 'common:syncWait.workerStoppedAndCancelled',
  'no-measured-advance': 'common:syncWait.aliveButNotAdvancingCancelled',
}

/** Lo que este catálogo sabe nombrar. Exportado para los guardianes de paridad. */
const CUSTOMER_FACING_FAILURE_CODES: readonly string[] = Object.keys(
  CUSTOMER_FACING_KEY_BY_FAILURE_CODE,
)

/** ¿El mensaje almacenado dice algo que la traducción del código no diga? */
function storedMessageAddsNothing(message: string | null | undefined): boolean {
  if (typeof message !== 'string') return true
  const recortado = message.trim()
  return recortado.length === 0 || CONTENT_FREE_STORED_MESSAGES.includes(recortado)
}

/**
 * La clave traducida que debe mostrarse, o `undefined` cuando gana el mensaje
 * almacenado (o cuando el código no está catalogado).
 *
 * `reapReason` sólo lo trae una siega (#2205) y sólo afina `STUCK_JOB_REAPED`;
 * para cualquier otro código es ruido y se ignora.
 */
export function customerFacingKeyForFailureCode(
  code: string | null | undefined,
  storedMessage: string | null | undefined,
  reapReason?: string | null,
): string | undefined {
  if (typeof code !== 'string') return undefined
  const recortado = code.trim()
  const clave = CUSTOMER_FACING_KEY_BY_FAILURE_CODE[recortado]
  if (clave === undefined) return undefined

  if (FAILURE_CODES_THAT_OUTRANK_THE_MESSAGE.includes(recortado)) {
    return refineByReapReason(recortado, clave, reapReason)
  }
  return storedMessageAddsNothing(storedMessage) ? clave : undefined
}

/** La frase del motivo cuando lo hay; si no, la genérica del código. */
function refineByReapReason(
  code: string,
  fallbackKey: string,
  reapReason: string | null | undefined,
): string {
  if (code !== 'STUCK_JOB_REAPED' || typeof reapReason !== 'string') return fallbackKey
  return KEY_BY_STUCK_JOB_REAP_REASON[reapReason.trim()] ?? fallbackKey
}
